import { useState, useEffect, useRef, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react'
import type { UIEvent } from 'react'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import AdminProductRow from './AdminProductRow'
import type { Product } from '../../types'

const MOBILE_ROW_H  = 165
const DESKTOP_ROW_H = 85
const OVERSCAN      = 5

export interface VirtualListHandle {
  scrollToTop: () => void
}

interface Props {
  items: Product[]
  onEdit: (id: string) => void
  onScrollChange?: (scrolled: boolean) => void
}

function AdminVirtualListInner(props: Props, ref: React.Ref<VirtualListHandle>) {
  const { items, onEdit, onScrollChange } = props
  const containerRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop]     = useState(0)
  const [viewHeight, setViewHeight]   = useState(600)

  const isMobile = useMediaQuery('(max-width: 639px)')
  const ROW_H    = isMobile ? MOBILE_ROW_H : DESKTOP_ROW_H

  useImperativeHandle(ref, () => ({
    scrollToTop: () => containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' }),
  }), [])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const update = () => {
      setViewHeight(window.innerHeight - el.getBoundingClientRect().top - 8)
    }
    update()
    const ro = new ResizeObserver(update)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const onScroll = useCallback((e: UIEvent<HTMLDivElement>) => {
    const top = e.currentTarget.scrollTop
    setScrollTop(top)
    onScrollChange?.(top > 300)
  }, [onScrollChange])

  const totalH   = items.length * ROW_H
  const firstIdx = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN)
  const lastIdx  = Math.min(items.length - 1, Math.ceil((scrollTop + viewHeight) / ROW_H) + OVERSCAN)

  const visible = useMemo(() => {
    const rows = []
    for (let i = firstIdx; i <= lastIdx; i++) rows.push(items[i])
    return { rows, firstIdx }
  }, [firstIdx, lastIdx, items])

  return (
    <div
      ref={containerRef}
      className="pb-12"
      style={{ height: viewHeight, overflowY: 'auto', position: 'relative' }}
      onScroll={onScroll}
    >
      <div style={{ height: totalH, position: 'relative' }}>
        {visible.rows.map((product, i) => (
          <div className='px-4'
            key={product.id}
            style={{
              position: 'absolute',
              top: (visible.firstIdx + i) * ROW_H,
              left: 0,
              right: 0,
              height: ROW_H - 16,
            }}
          >
            <AdminProductRow
              product={product}
              onEdit={() => onEdit(product.id)}
            />
          </div>
        ))}
      </div>
    </div>
  )
}

const AdminVirtualList = forwardRef(AdminVirtualListInner)
AdminVirtualList.displayName = 'AdminVirtualList'

export default AdminVirtualList
